import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../hooks';
import { config } from '../config';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  fallback?: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRoles,
  fallback
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // 🪪 Token-based login: this page logs out any current session and signs
  // in with the token in the URL. It must render regardless of who (if
  // anyone) is currently signed in — never redirect away from it.
  if (currentPath === '/auth/token-login') {
    return <>{children}</>;
  }

  // Legacy entry alias — the ECB's configured home (and older links/bookmarks)
  // point at /student/login; send it to the canonical auth login.
  if (currentPath === '/student/login') {
    return <Navigate to="/auth/login" replace />;
  }

  // Show loading while checking auth
  if (isLoading) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const { authOnly, public: publicRoutes } = config.routes;

  // Prefix-aware match — `/auth/login` matches `/auth/login`, `/about/x`
  // matches public `/about`, etc. Lets us list a parent path once
  // instead of every nested route.
  const matchesAny = (paths: readonly string[], p: string) =>
    paths.some((rule) => p === rule || p.startsWith(rule + '/'));

  // 🏢 Single-company profile — /companies/:id (printed QR codes, share
  // links) renders logged out too: companies/[id].tsx shows the
  // CompanyPublicTeaser instead of the full dashboard when unauthenticated.
  // Scoped to exactly ONE path segment after /companies/ so the 1545-record
  // list at bare /companies stays fully gated — do not widen this to
  // `startsWith('/companies/')` or every future /companies/* admin route
  // (bulk import, etc.) goes public with it.
  const isSingleCompanyProfile = /^\/companies\/[^/]+$/.test(currentPath);

  // 🔒 Pre-login middleware: Block auth-only routes if authenticated
  if (isAuthenticated && matchesAny(authOnly, currentPath)) {
    // Support agents (moderator.approve) are platform-tier with no tenant, so
    // the shared authenticatedRedirect (/dashboard) would land them on a
    // tenant surface they cannot use. Send them to the one page they can
    // reach — PlatformLayoutRoute would bounce them here anyway.
    const u = user as unknown as { role?: string; level?: string } | null;
    if (u?.role === 'moderator' && u?.level === 'approve') {
      return <Navigate to="/platform/support" replace />;
    }
    return <Navigate to={config.auth.redirects.authenticatedRedirect} replace />;
  }

  // 🔓 Post-login middleware: Block protected routes if not authenticated
  if (
    !isAuthenticated &&
    !isSingleCompanyProfile &&
    !matchesAny(publicRoutes, currentPath) &&
    !matchesAny(authOnly, currentPath)
  ) {
    // login.tsx already reads `?next=` (open-redirect-safe: same-origin
    // paths only) to send the TPO back where they came from once
    // `isAuthenticated` flips true — it just never received it before.
    // `state={{from}}` is left in place too since nothing currently reads
    // it, but a future consumer may.
    const next = encodeURIComponent(currentPath + location.search);
    return <Navigate
      to={`${config.auth.redirects.loginPage}?next=${next}`}
      state={{ from: location }}
      replace
    />;
  }

  // 🎓 Student-scope gate: signed-in users with role 'user' (i.e. students)
  // are restricted to /student* and the public/auth surfaces. Any TPO
  // page (chat, students, drives, companies, plans, dashboard, etc.)
  // bounces them back to their welcome page. Defence-in-depth on top of
  // the post-login routing in login.tsx.
  if (isAuthenticated && user) {
    const role = (user as any).role as string | undefined;
    const isStudent = role === 'user' || role === 'basic';
    if (isStudent) {
      const studentAllowed =
        currentPath === '/student' ||
        currentPath.startsWith('/student/') ||
        currentPath === '/notifications' ||  // shared with TPO — same enable + test page
        currentPath === '/logout' ||
        currentPath === '/' ||
        matchesAny(publicRoutes, currentPath) ||
        matchesAny(authOnly, currentPath);
      if (!studentAllowed) {
        return <Navigate to="/student" replace />;
      }
    }
  }

  // 🛟 Support-scope gate: a support agent (moderator.approve) is platform-tier
  // with no tenant, so every TPO surface — dashboard, chat, students, drives —
  // is empty or misleading for them. Confine them to the ticket queue. This is
  // the mirror of the student gate above: PlatformLayoutRoute covers
  // /platform/*, and this covers everything outside it.
  if (isAuthenticated && user) {
    const u = user as unknown as { role?: string; level?: string };
    if (u.role === 'moderator' && u.level === 'approve') {
      const supportAllowed =
        currentPath === '/platform/support' ||
        currentPath.startsWith('/platform/support/') ||
        // Paper authoring. A support agent writes master papers, which go to a
        // platform admin for approval before reaching the library (reviewState
        // on Test). /tests is master-scoped and returns 200 without a tenant —
        // the same reason CollegeLayoutRoute leaves it ungated — so unlike the
        // TPO surfaces this gate is protecting them from, it is neither empty
        // nor misleading for them.
        currentPath === '/tests' ||
        currentPath.startsWith('/tests/') ||
        // Peer review: a support agent approves colleagues' papers here.
        currentPath === '/platform/paper-reviews' ||
        currentPath === '/logout' ||
        matchesAny(publicRoutes, currentPath) ||
        matchesAny(authOnly, currentPath);
      if (!supportAllowed) {
        return <Navigate to="/platform/support" replace />;
      }
    }
  }

  // Role-based access control with role.level combinations
  if (isAuthenticated && requiredRoles?.length && user) {
    const userPermission = `${user.role}.${user.level}`;
    const hasRequiredRole = requiredRoles.includes(userPermission);

    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
            <div className="text-sm text-gray-500">
              <p>Your role: <span className="font-mono">{userPermission}</span></p>
              <p>Required: <span className="font-mono">{requiredRoles.join(' OR ')}</span></p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

// Keep legacy ProtectedRoute for backward compatibility
export const ProtectedRoute = AuthGuard;